/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useEffect, useRef } from 'react';
import { NavigationType, useLocation, useNavigationType } from 'react-router';
import { usePageDesignerMode } from '@salesforce/storefront-next-runtime/design/react/core';
import { CHAT_LINK_HREF, COOKIE_SETTINGS_HREF } from '@/lib/link/link-picker';

/**
 * Hashes that are interceptor sentinels rather than anchor targets — the link picker resolves
 * `type: 'chat'` / `type: 'cookieSettings'` to these, and a listener turns the click into an action
 * (see src/lib/link/link-picker.ts). No element carries these ids, so scrolling must not chase them.
 */
const SENTINEL_HASHES = new Set<string>([CHAT_LINK_HREF, COOKIE_SETTINGS_HREF, '#openlink-openchat']);

/** How often the watchdog re-checks the target's position. */
const TICK_MS = 100;

/** Consecutive on-target checks that count as "the layout stopped moving". */
const STABLE_TICKS = 3;

/**
 * How long the watchdog stays on duty, counted only in ticks where the document was actually
 * scrollable. A scroll lock pauses this budget instead of burning it — see {@link isScrollLocked}.
 */
const SETTLE_TIMEOUT_MS = 20_000;

/** Wall-clock ceiling, so a document that is never unlocked can't leave the interval running. */
const ABSOLUTE_TIMEOUT_MS = 60_000;

/**
 * How long to keep waiting for a target that does not exist once the document has finished loading.
 * A stale or renamed id would otherwise poll for the whole settle budget for nothing. Generous
 * enough to cover a lazy Page Designer chunk still arriving after `load` on a slow connection —
 * measured at ~400ms after `load` on a local production build.
 *
 * Applies to the **document load only**. `document.readyState` reaches `'complete'` once and never
 * returns to `'loading'`, so on a client-side navigation it is already `'complete'` when the watchdog
 * arms — the grace would start counting against a page whose lazy chunks haven't been requested yet
 * and, on a slow connection, expire before the target exists. Client navigations are bounded by
 * {@link SETTLE_TIMEOUT_MS} instead.
 */
const MISSING_TARGET_GRACE_MS = 5_000;

/**
 * Events that mean the shopper took over the scroll position. Deliberately NOT `scroll` — our own
 * `scrollTo` fires that, which would abort the watchdog on its first correction.
 */
const USER_INTENT_EVENTS = ['wheel', 'touchmove', 'keydown', 'pointerdown'] as const;

/**
 * True while something has pinned the scrollport — a third-party overlay (Attentive's SMS creative
 * sets `overflow: hidden; position: absolute; inset: 0` on both `<html>` and `<body>`), a vaul
 * bottom sheet (`position: fixed` on `<body>`), or a Radix modal (`overflow: hidden`).
 *
 * While that is true `window.scrollTo` is a no-op and `scrollY` reads 0, so the watchdog can neither
 * measure nor act. `overflow-x` is ignored on purpose: clipping the horizontal axis is common and
 * says nothing about vertical scrolling.
 */
function isScrollLocked(): boolean {
    return [document.documentElement, document.body].some((element) => {
        const style = getComputedStyle(element);
        return style.overflowY === 'hidden' || style.position === 'fixed' || style.position === 'absolute';
    });
}

/**
 * Where the document must be scrolled for `element` to sit just below the sticky header — the same
 * place the browser's own fragment scroll would land it, because both honour the scrolling element's
 * `scroll-padding-top` (set in src/theme/base.css from `--header-height`).
 *
 * Clamped to the scrollable range so a target near the end of a short page yields a position the
 * document can actually reach; otherwise the watchdog would never see itself arrive.
 */
function desiredScrollTop(element: Element): number {
    const offset = parseFloat(getComputedStyle(document.documentElement).scrollPaddingTop) || 0;
    const documentTop = element.getBoundingClientRect().top + window.scrollY;
    const maxScroll = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
    return Math.min(Math.max(0, documentTop - offset), maxScroll);
}

/**
 * Keeps an anchor link (`/about#FAQ`) on its target until the page settles there (UCR-3082).
 *
 * The browser's built-in fragment scroll is a one-shot, and on this storefront three things happen
 * after it fires that each throw the shopper back to the top:
 *
 * 1. **Streamed SSR** — the browser scrolls as soon as the target parses, while Suspense boundaries
 *    above it are still empty. Every one that resolves pushes the target further down.
 * 2. **Page Designer hydration** — every PD component is a lazy chunk, so on hydration the registry
 *    has none of them and the component wrapper re-suspends (`throw registry.preload(...)` in
 *    src/components/region/component.tsx). React discards the server markup and shows a fallback
 *    until the chunks land: measured on `/about`, `<main>` went 6130px → 247px → 5443px, and a
 *    document with no height has no scroll offset left to keep.
 * 3. **Third-party overlays** — Attentive's SMS creative locks the scrollport ~2s in without
 *    preserving the offset, so the position is lost again when it appears.
 *
 * So the scroll target is owned here instead of being left to a single event: re-apply until the
 * target's own position stops moving AND the document is genuinely scrollable, then stand down.
 * React Router's `<ScrollRestoration>` is not a substitute — on a document load it skips its hash
 * branch entirely (`restoreScrollPosition` is `false` when hydration data exists), and on a client
 * navigation it runs once at commit, when a lazy PD target usually doesn't exist yet.
 *
 * **Yields to the shopper.** Any {@link USER_INTENT_EVENTS} ends the watchdog — except while the
 * document is locked, where a tap or keystroke is someone using the overlay (closing the popup,
 * typing their number) and cannot be a scroll. That exception is what lets the anchor still resolve
 * after the SMS popup is dismissed.
 *
 * Keyed on pathname + hash, and skipped on back/forward: `/about#contact` → `/learn#contact` is a
 * real anchor navigation the hash alone can't see (both pages can own a `#contact`), while
 * back/forward to a hashed URL must keep the position `<ScrollRestoration>` restored rather than
 * being yanked to the anchor again.
 *
 * Telling those apart is **not** as simple as `navigationType === POP`, for two reasons that a naive
 * guard gets wrong in opposite directions:
 *
 * 1. The **initial document load also reports `POP`** (react-router's history starts at
 *    `Action.Pop`), and that is the case this component exists for.
 * 2. A one-shot "first run" latch can't stand in for it either — React StrictMode double-invokes
 *    every effect in development (`development.strictMode` is on in `config.server.ts`), so
 *    invocation 1 consumes the latch, its cleanup clears the interval, and invocation 2 bails: the
 *    watchdog ends up disarmed in `pnpm dev` while production, being single-invoke, works.
 *
 * So the skip compares **locations** instead: back/forward is a `POP` onto a location whose `key`
 * differs from the last one seen. A StrictMode re-invoke carries the same `key` and re-arms; the
 * initial load (and a reload of a URL reached via PUSH, whose `key` is *not* `'default'`) has no
 * previous key at all and arms.
 *
 * Because `key` is part of the effect's dependencies, any new history entry re-arms — including
 * re-clicking the link for the URL already on screen, which is what someone clicking it again wants.
 * The flip side is that a same-page navigation that only changes search params while a hash is
 * present would also re-park the shopper on the anchor; no such surface exists today (the pages that
 * navigate by search param — PLP, search — carry no anchors).
 *
 * Renders nothing. Mounted once in root.tsx, inside `<PageDesignerProvider>` so the design-mode
 * guard sees the real mode and never fights the Page Designer editor.
 */
export default function HashAnchorScroll(): null {
    const { pathname, hash, key } = useLocation();
    const navigationType = useNavigationType();
    const { isDesignMode } = usePageDesignerMode();
    /** The last location `key` this component saw — `null` until the first one. */
    const seenKeyRef = useRef<string | null>(null);
    /** Whether any *real* navigation has happened, as opposed to a re-run for the location we're on. */
    const hasNavigatedRef = useRef(false);

    useEffect(() => {
        // Recorded before every early return, so "the location we last saw" stays accurate even for a
        // location this component decided not to act on.
        const previousKey = seenKeyRef.current;
        const isNewLocation = previousKey !== null && previousKey !== key;
        seenKeyRef.current = key;
        if (isNewLocation) hasNavigatedRef.current = true;

        if (isDesignMode) return;
        // Back/forward — leave the position <ScrollRestoration> restored alone. Compares locations
        // rather than counting runs; see the docblock for why POP alone and a first-run latch both fail.
        if (navigationType === NavigationType.Pop && isNewLocation) return;
        if (!hash || hash === '#' || SENTINEL_HASHES.has(hash)) return;

        let targetId: string;
        try {
            targetId = decodeURIComponent(hash.slice(1));
        } catch {
            // Not a decodable element id — the same bail-out React Router makes for this case.
            return;
        }
        if (!targetId) return;

        // The grace below is for a stale id on a document load; a client navigation can't use
        // `readyState` as a signal at all (see MISSING_TARGET_GRACE_MS).
        const isDocumentLoad = !hasNavigatedRef.current;
        const startedAt = Date.now();
        let unlockedElapsed = 0;
        let missingSinceLoaded = 0;
        let stableTicks = 0;
        let stopped = false;
        let timer: ReturnType<typeof setInterval> | undefined;

        const stop = () => {
            stopped = true;
            if (timer !== undefined) clearInterval(timer);
            for (const type of USER_INTENT_EVENTS) {
                window.removeEventListener(type, onUserIntent, { capture: true });
            }
        };

        function onUserIntent() {
            if (isScrollLocked()) return;
            stop();
        }

        const tick = () => {
            if (Date.now() - startedAt > ABSOLUTE_TIMEOUT_MS) {
                stop();
                return;
            }
            // Locked: can't measure, can't scroll. Hold position (and the budget) until it releases.
            if (isScrollLocked()) {
                stableTicks = 0;
                return;
            }
            unlockedElapsed += TICK_MS;
            if (unlockedElapsed > SETTLE_TIMEOUT_MS) {
                stop();
                return;
            }
            const element = document.getElementById(targetId);
            // Target not rendered yet (streaming, or a lazy PD chunk still in flight) — keep waiting,
            // but not forever once the document has loaded: a stale or renamed id never arrives.
            if (!element) {
                stableTicks = 0;
                if (isDocumentLoad && document.readyState === 'complete') {
                    missingSinceLoaded += TICK_MS;
                    if (missingSinceLoaded > MISSING_TARGET_GRACE_MS) stop();
                }
                return;
            }
            missingSinceLoaded = 0;
            const desired = desiredScrollTop(element);
            // Off target, either because the layout moved or because something reset the scroll.
            if (Math.abs(window.scrollY - desired) > 1) {
                window.scrollTo({ top: desired, left: window.scrollX, behavior: 'auto' });
                stableTicks = 0;
                return;
            }
            stableTicks += 1;
            if (stableTicks >= STABLE_TICKS) stop();
        };

        for (const type of USER_INTENT_EVENTS) {
            window.addEventListener(type, onUserIntent, { capture: true, passive: true });
        }
        // Run once up front so a client navigation to already-loaded content lands immediately
        // instead of a tick late.
        tick();
        if (!stopped) timer = setInterval(tick, TICK_MS);

        return stop;
    }, [pathname, hash, key, navigationType, isDesignMode]);

    return null;
}
